import { DatePipe, NgClass } from '@angular/common';
import { buildDeleteConfig } from '../../../../../utils/delete-confirmation';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { measureCheckerDefinition } from '../../../../../entity-definitions/measure-checker.definition';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { MeasureDetailService } from '../../../../../services/measure-detail.service';
import { TestModeService } from '../../../../../services/test-mode.service';
import { ToastService } from '../../../../../services/toast.service';
import { isEntityFormDeleteAction } from '../../../../../types/entity-form-result.interface';
import { CheckerOptionsAll, CheckerType, MeasureChecker } from '../../../../../types/measure-checker.interface';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { CesButtonSecondaryComponent, CesDeleteButtonComponent } from '../../../../ui/ces-buttons';
import { CesProperty } from '../../../../ui/ces-property/ces-property.component';
import { FormulaExpressionComponent } from '../../../../ui/expression-display/expression-display.component';
import { ParameterMode } from '../../../../ui/inputs/parameters-input/parameter-types';
import { ParametersDisplayComponent } from '../../../../ui/parameters-display/parameters-display.component';

@Component({
  selector: 'app-measure-checker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionModule,
    ButtonModule,
    CardModule,
    CesButtonSecondaryComponent,
    CesDeleteButtonComponent,
    CesProperty,
    DatePipe,
    NgClass,
    DialogModule,
    DividerModule,
    DropdownModule,
    FormsModule,
    FormulaExpressionComponent,
    InputNumberModule,
    InputTextModule,
    ParametersDisplayComponent,
    ReactiveFormsModule,
    RouterModule,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TranslocoModule,
  ],
  templateUrl: './measure-checker.component.html',
  styleUrl: './measure-checker.component.scss',
})
export class MeasureCheckerComponent implements OnInit {
  protected readonly _FRAGMENT_PREFIX_ = 'checker-';
  protected readonly CheckerType = CheckerType;
  protected readonly ParameterMode = ParameterMode;

  protected readonly dataService = inject(MeasureDetailService);
  private readonly toastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialogService = inject(EntityDialogService);
  protected readonly t = inject(TestModeService);
  private readonly translateService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly selectedCheckerId = signal<number | null>(null);

  protected readonly measure = this.dataService.measure;
  protected readonly measureCheckers = this.dataService.measureCheckers;
  protected readonly measureSortedCheckers = this.dataService.measureSortedCheckers;
  protected readonly units = this.dataService.units;

  protected readonly selectedChecker = computed(() => this.measureCheckers().find(c => c.id === this.selectedCheckerId()));

  protected readonly writePermission = computed(() => ({
    permissionId: PermissionScope.Measures,
    type: 'write' as const,
    assetId: this.measure()?.assetId,
  }));

  protected readonly otherMeasures = computed(() => {
    const currentMeasure = this.measure();
    if (!currentMeasure) return [];
    return this.dataService.measures().filter(m => m.assetId === currentMeasure.assetId && m.id !== currentMeasure.id);
  });

  protected readonly measures = computed(() => {
    const currentMeasure = this.measure();
    if (!currentMeasure) return [];
    return this.dataService.measures().filter(m => m.assetId === currentMeasure.assetId);
  });

  get CheckerOptions() {
    return CheckerOptionsAll.map(opt => ({
      name: this.translateService.translate(opt.labelKey),
      value: opt.value,
    }));
  }

  protected readonly isCurrentChecker = (checker: MeasureChecker): boolean => {
    const now = new Date();
    const startDate = new Date(checker.startTs);
    const nextChecker = this.measureSortedCheckers().find(c => c.startTs > checker.startTs && c.id !== checker.id);
    const endDate = nextChecker ? new Date(nextChecker.startTs) : checker.endTs ? new Date(checker.endTs) : null;
    return startDate <= now && (!endDate || endDate > now);
  };

  protected readonly getCheckerTypeName = (type: CheckerType): string =>
    this.CheckerOptions.find(checker => checker.value === type)?.name ?? '';

  ngOnInit(): void {
    this.checkFragmentAndSelectChecker();

    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fragment => {
      if (fragment?.startsWith(this._FRAGMENT_PREFIX_)) {
        const id = Number(fragment.replace(this._FRAGMENT_PREFIX_, ''));
        if (!isNaN(id) && this.measureCheckers().some(c => c.id === id)) {
          this.selectedCheckerId.set(id);
        }
      } else if (!this.selectedCheckerId()) {
        const first = this.measureCheckers()[0];
        if (first) this.selectChecker(first.id);
      }
    });
  }

  private checkFragmentAndSelectChecker(): void {
    const fragment = this.route.snapshot.fragment;
    if (fragment?.startsWith(this._FRAGMENT_PREFIX_)) {
      const id = Number(fragment.replace(this._FRAGMENT_PREFIX_, ''));
      if (!isNaN(id) && this.measureCheckers().some(c => c.id === id)) {
        this.selectedCheckerId.set(id);
        return;
      }
    }
    const first = this.measureCheckers()[0];
    if (first) this.selectChecker(first.id);
  }

  selectChecker(id: number): void {
    this.selectedCheckerId.set(id);
    void this.router.navigate([], {
      fragment: this._FRAGMENT_PREFIX_.concat(String(id)),
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  protected deleteChecker(entity: MeasureChecker): void {
    if (!entity) return;

    this.dataService
      .deleteChecker(entity.id)
      .then(() => {
        if (this.selectedCheckerId() === entity?.id) {
          const next = this.measureCheckers()[0];
          if (next) {
            this.selectChecker(next.id);
          } else {
            this.selectedCheckerId.set(null);
            void this.router.navigate([], { relativeTo: this.route });
          }
        }
      })
      .catch(() => this.toastService.error(this.translateService.translate('entities.measureChecker.messages.deleteError')));
  }

  createChecker(): void {
    const measure = this.measure();
    if (!measure) return;

    this.dialogService
      .openCreateDialog<MeasureChecker>({
        header: this.translateService.translate('entities.measureChecker.messages.createTitle'),
        entityDefinition: measureCheckerDefinition,
        mode: 'create',
        size: 'large',
        additionalData: {
          measureId: measure.id,
          units: this.units(),
          measures: this.otherMeasures(),
          selectedMeasureChecker: this.selectedChecker(),
          checkers: this.measureCheckers(),
        },
      })
      .subscribe(checkerData => {
        if (!checkerData || isEntityFormDeleteAction(checkerData)) return;
        void (async () => {
          try {
            const created = await this.dataService.createChecker(checkerData as Omit<MeasureChecker, 'id'>);
            if (measure.id) await this.dataService.loadData(measure.id);
            if (created?.id) this.selectChecker(created.id);
          } catch (error) {
            console.error('Error creating checker', error);
            this.toastService.error(this.translateService.translate('entities.measureChecker.messages.createError'));
          }
        })();
      });
  }

  editChecker(checker: MeasureChecker): void {
    const measure = this.measure();
    if (!measure) return;

    const formattedDate = this.formatLocalDate(checker.startTs);
    this.dialogService
      .openEditDialog<MeasureChecker>(checker, {
        header: this.translateService.translate('entities.measureChecker.messages.editTitle', { date: formattedDate }),
        entityDefinition: measureCheckerDefinition,
        mode: 'edit',
        size: 'large',
        additionalData: {
          measureId: measure.id,
          units: this.units(),
          measures: this.otherMeasures(),
          checkers: this.measureCheckers(),
        },
        onDelete: entity => this.deleteChecker(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if (isEntityFormDeleteAction(result)) {
          this.deleteChecker(result.entity);
          return;
        }
        void (async () => {
          try {
            const checkerId = result['id'];
            if (checkerId === undefined) return;
            await this.dataService.updateChecker(checkerId, result);
            if (measure.id) await this.dataService.loadData(measure.id);
          } catch (error) {
            console.error('Error updating checker', error);
            this.toastService.error(this.translateService.translate('entities.measureChecker.messages.updateError'));
          }
        })();
      });
  }

  protected getCheckerDeleteConfig(checker: MeasureChecker) {
    return buildDeleteConfig('entities.measureChecker.messages.deleteConfirm', checker.name);
  }

  protected getCheckerFromDate(startTs: string): string {
    const date = this.formatLocalDate(startTs);
    return this.translateService.translate('entities.measureChecker.messages.fromDate', { date });
  }

  private formatLocalDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
